// Global search for the command palette: the two real list endpoints,
// capped small. There is no server-side document search, which is why no
// documents group exists anywhere.

import { getAuthHeaders } from "./auth";
import { adaptCaseToMatter, clientEmailOf, type ServerCaseShape } from "./mock";
import type { ClientListRow } from "./clients";
import type { Matter } from "../data/types";

export interface MatterHit {
  matter: Matter;
  /** Mongo _id of the case. The adapted Matter carries the eID, which is
      what a person reads, not what a write route takes. */
  caseId: string;
  /** Empty when the firm holds no address for this client. */
  clientEmail: string;
}

export interface GlobalSearchResults {
  matters: MatterHit[];
  clients: ClientListRow[];
}

// Both routes run an unindexed regex scan; keep the page small. Cases
// are over-fetched because the closed-matter filter runs client-side -
// the newest hits can all be archive churn from the Excel sync.
const CASE_FETCH_LIMIT = 24;
const RESULT_CAP = 8;

export async function searchGlobal(q: string): Promise<GlobalSearchResults> {
  const enc = encodeURIComponent(q);
  const init = {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin" as RequestCredentials,
  };
  const [casesRes, clientsRes] = await Promise.all([
    fetch(`/api/cases?search=${enc}&limit=${CASE_FETCH_LIMIT}`, init),
    fetch(`/api/clients?search=${enc}&limit=${RESULT_CAP}`, init),
  ]);
  if (!casesRes.ok || !clientsRes.ok) {
    throw new Error(`Search failed (${casesRes.status}/${clientsRes.status})`);
  }
  const casesBody = (await casesRes.json()) as { cases?: ServerCaseShape[] };
  const clientsBody = (await clientsRes.json()) as { clients?: ClientListRow[] };
  return {
    // The Excel sync archives cases that drop out of the sheet; a closed
    // matter is not a jump target (same rule as CasesIndex).
    matters: (casesBody.cases ?? [])
      .map((c) => ({
        matter: adaptCaseToMatter(c),
        caseId: String(c._id),
        clientEmail: clientEmailOf(c),
      }))
      .filter((h) => h.matter.status !== "closed")
      .slice(0, RESULT_CAP),
    clients: clientsBody.clients ?? [],
  };
}
